/** Client-side preferences — the ones the service has no opinion about.
 *
 *  `/v1/settings` covers collection visibility and nothing else, so week start
 *  and day-of-year live here beside the theme rather than pretending to be
 *  server state. Same shape as `theme.ts`: read, write, no store.
 *
 *  Deliberately not here: the reference Settings screen shows a
 *  `Confirm before cancelling` toggle whose own help text says cancel and delete
 *  *always* ask twice. A toggle for invariant behaviour is a contradiction, so
 *  the invariant stays and the toggle does not exist.
 */

import type { WeekStart } from '../api/week';

export interface Preferences {
  weekStart: WeekStart;
  /** `DAY 214` on the day header, as the terminal client shows it. */
  showDayOfYear: boolean;
  /** Two days where the width allows, or one on purpose.
   *
   *  `auto` lets width, orientation and R12's measurement decide. `focus` pins
   *  one day whatever the width — the terminal client's own toggle between the
   *  two-day spread and the one-day focus, and the same reason for it: two days
   *  in a portrait column is more journal but less legible, and which of those
   *  you want is a preference rather than something a breakpoint can know. */
  dayView: 'auto' | 'focus';
}

const KEY = 'bujo.preferences.v1';

const DEFAULTS: Preferences = {
  // Monday by default, per the product contract. Ranges stay inclusive either
  // way, and there is still no "this week" rollup.
  weekStart: 'monday',
  showDayOfYear: true,
  dayView: 'auto',
};

export function readPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return {
      weekStart: parsed.weekStart === 'sunday' ? 'sunday' : 'monday',
      showDayOfYear: parsed.showDayOfYear !== false,
      dayView: parsed.dayView === 'focus' ? 'focus' : 'auto',
    };
  } catch {
    return DEFAULTS;
  }
}

export function writePreferences(next: Preferences): void {
  localStorage.setItem(KEY, JSON.stringify(next));
}
